<script lang="ts" setup>
    import { onMounted, ref, reactive, computed} from 'vue';
    import ChoiceTable from "../../components/ChoiceTable.vue";
    import ActionButtonV2 from '../../components/ActionButtonV2.vue'
    import MessageBadge from "../../components/Messages/MessageBadge.vue"
    import ModalDialog from '../../components/ModalDialog.vue'
    import RadioButtonInput from "../../components/RadioButtonInput.vue"
    import Tab from "../../components/Tab.vue"
    import Icon from '../../components/Icon.vue'
    import Page from '../../components/Page.vue'
    import SearchPatient from '../../components/Search/SearchPatient.vue'
    import MessageSection from '../../components/Messages/MessageSection.vue'
    import ResizeMonitor from '../../components/ResizeMonitor.vue'
    import { IEHIExportQueueItem, IEHIExportQueueResult, EHIExportStatus, IEHIExportLinkItem, EHIExportRequestType, EHIExportTabName, IEHIExportMessageBadgeItem, IEHIAuditItem, EHIExportAuditType} from '../../lib/Models/Export/EHIExportModel';
    import { ButtonDisplaySize, ButtonDisplayStyle, ModalSize, IconTypes, DisplayStyle, HoverCursorTypes, LayoutOrientation, MessageSectionType, AlignmentTypeStyle, PaginationButtonSizeStyle, TableRowType } from "../../Enums/UiEnums";
    import { ICol, ValueStrDelegate } from "../../lib/ChoiceTableHelpers";
    import { ISearchPatientKey, ISearchPatientItem } from '../../lib/Models/SearchModel';
    import { ActionButtonStyle } from "../../lib/Types/Types";
    import { PrivilegeType, AppScreens } from '../../Enums/ChoiceEnums';
    import * as ChoiceApi from '../../lib/ChoiceApi';
    import { popAlert, confirmDialog } from '../../lib/Notification';
    import { initPinia } from '../../lib/PiniaMgr';
    import { usePrivilegesStore } from '../../lib/Stores/PrivilegesStore'    
    import '../../../css/choice.css';

    const formData = reactive<{
        gridRowCount: number,
        activeBaseData: IEHIExportQueueItem[],
        inactiveBaseData: IEHIExportQueueItem[],
        auditBaseData: IEHIAuditItem[],
        chartNumber?: number,
        showLinks: boolean,
        showNewExport: boolean,
        showAudit: boolean,
        showPracticeExportMessage: boolean,
        exportLinks?: IEHIExportLinkItem[],
        currentPatientName?: string,
        exportType: EHIExportRequestType,
        tabName: EHIExportTabName,
        requestError: boolean,
        requestErrorMessage: string,
        defaultPatientKey?: ISearchPatientKey,
        activeTopItem: number,
        inactiveTopItem: number
    }>({
        gridRowCount: 0,
        activeBaseData: [],
        inactiveBaseData: [],
        auditBaseData: [],
        showLinks: false,
        showNewExport: false,
        showAudit: false,
        showPracticeExportMessage: false,
        exportType: EHIExportRequestType.Patient,
        tabName: EHIExportTabName.Active,
        requestError: false,
        requestErrorMessage: "",
        activeTopItem: 0,
        inactiveTopItem: 0
    });

    const linkKey = ref(0);
    const privilegesStore = usePrivilegesStore(initPinia());
   
    const FormatType: ValueStrDelegate = (value: any, col: ICol, row: Record<string, any> | undefined, rowType: TableRowType): string | undefined => {
        if (rowType === TableRowType.Body)
        {
             return EHIExportAuditType[row?.auditTypeId];
        }
    }

    const messageBadgeConfiguration: Record<EHIExportStatus, IEHIExportMessageBadgeItem> =
    {
        [EHIExportStatus.Open]: { tooltip: "The export is waiting to be built.", overrideStyle: "statusYellow", display: "Queued"},
        [EHIExportStatus.Processing]: { tooltip: "The export is processing.", overrideStyle: "statusBlue", display: "In Process"},
        [EHIExportStatus.Completed]: { tooltip: "The export is ready to export.", overrideStyle: "statusGreen", display: "Completed"},
        [EHIExportStatus.Expired]: { tooltip: "The export expired.", overrideStyle: "statusYellow", display: "Expired"},
        [EHIExportStatus.Cancelled]: { tooltip: "The export was cancelled.", overrideStyle: "statusRed", display: "Cancelled"},
        [EHIExportStatus.Failed]: { tooltip: "The export failed to build.", overrideStyle: "statusRed", display: "Failed"},
    };

    const activeTableColumns = ref<ICol[]>([
        { Name: 'Patient', DataField: 'patientName' },
        { Name: 'Patient ID', DataField: 'patientId' },
        { Name: 'Date of Birth', DataField: 'birthDate' },
        { Name: 'Date Requested', DataField: 'postedDate' },
        { Name: 'Requested By', DataField: 'userName' },
        { Name: 'Status', DataField: 'statusId' },
        { Name: 'Action', DataField: 'empty' },
    ]);

    const inactiveTableColumns = ref<ICol[]>([
        { Name: 'Patient', DataField: 'patientName' },
        { Name: 'Patient ID', DataField: 'patientId' },
        { Name: 'Date of Birth', DataField: 'birthDate' },
        { Name: 'Date Requested', DataField: 'postedDate' },
        { Name: 'Requested By', DataField: 'userName' },
        { Name: 'Status', DataField: 'statusId' }
    ]);

    const auditTableColumns = ref<ICol[]>([
        { Name: 'Patient', DataField: 'patientName' },
        { Name: 'Patient ID', DataField: 'patientId' },
        { Name: 'Date of Birth', DataField: 'birthDate' },
        { Name: 'Date Audited', DataField: 'auditDate' },
        { Name: 'Requested By', DataField: 'userName' },
        { Name: 'Audit Type', DataField: 'auditTypeId', FormatValue: FormatType }
    ]);

    const props = defineProps<{
        activeQueueResult: IEHIExportQueueResult,
        inactiveQueueResult: IEHIExportQueueResult
    }>();

    onMounted(async () => {
        formData.gridRowCount = calculateGridRowCount();
        await privilegesStore.getScreens([AppScreens.EHIExport, AppScreens.EHIPracticeExport]);
        formData.activeBaseData = props.activeQueueResult.taskList;
        formData.inactiveBaseData = props.inactiveQueueResult.taskList;
    });

    function windowResize() {
        formData.gridRowCount = calculateGridRowCount();
    }

    const exportSinglePatient = computed<boolean>(() => formData.exportType == EHIExportRequestType.Patient);
    const writePrivilege = computed<boolean>(() => privilegesStore.screens[AppScreens.EHIExport] >= PrivilegeType.ReadUpdateWrite ? true : false);
    const practiceExportPrivilege = computed<boolean>(() => privilegesStore.screens[AppScreens.EHIPracticeExport] >= PrivilegeType.ReadUpdateWrite ? true : false);

    function calculateGridRowCount() {
        const rowHeight = 29;
        const surroundingHeight = 250;
        let rowCount = ((window.innerHeight - surroundingHeight) / rowHeight) | 0;
        if (rowCount < 3) 
            rowCount = 3;
        return rowCount;
    }

    async function onShowLinks(row: IEHIExportQueueItem) {
        formData.currentPatientName = row.patientName;
        formData.exportLinks = await ChoiceApi.ehiExport.getExportLinks(row.queuePatientRecordId);
        linkKey.value += 1;
        formData.showLinks = true;
    }
    async function onHideLinks() {
        formData.showLinks = false;
    }

    async function onCancel(row: IEHIExportQueueItem) {
        const msg = `Are you sure you want to cancel the export for patient: ${row.patientName} ?`;
        const result = await confirmDialog(msg, { RejectText: "Cancel", ConfirmText: "Confirm" });
        if (result) {
            const post_data = {
                queuePatientRecordId: row.queuePatientRecordId
            }
            const response = await ChoiceApi.ehiExport.cancelTaskListItem(post_data);
            if (!response?.passed) {
                popAlert(response?.message ?? "Failed to cancel export.", "Cancel Export");
                return;
            }
            const index = formData.activeBaseData.findIndex(element => element.queuePatientRecordId == row.queuePatientRecordId);
            if (index > -1) {
                formData.activeBaseData.splice(index, 1);
            }
        }
    }

    function BuildLink(linkId: number, linkName: string) {
        return `../../api/export/b10/DownloadTaskLink/${linkId}/${linkName}`;
    }

    async function onNewExport() {
        formData.requestError = false;
        formData.requestErrorMessage = "";
        formData.defaultPatientKey  = {
            patientId: 0,
            display: ""
        }
        formData.exportType = EHIExportRequestType.Patient;
        formData.showNewExport = true;
        formData.showPracticeExportMessage = false;
    }
    async function onHideNewExport() {
        formData.showNewExport = false;
        formData.showPracticeExportMessage = false;
    }

    async function onAudit() {
        const result = await ChoiceApi.ehiExport.getAuditList();
        formData.auditBaseData = result || [];
        formData.showAudit = true;
    }
    async function onHideAudit() {
        formData.showAudit = false;
    }

    async function onPatientSearch(searchResultItem: ISearchPatientItem) {
        if (!searchResultItem)
            return;
        formData.defaultPatientKey = {
            patientId: searchResultItem.patient_id,
            display: searchResultItem.patient_name
        }
    }

    async function onCancelExportRequest() {
        onHideNewExport();
    }

    async function onSaveExportRequest() : Promise<void> {
        let patientId = formData.defaultPatientKey?.patientId ?? 0;
        if (formData.exportType == EHIExportRequestType.Practice) {
            if (practiceExportPrivilege.value) {
                formData.showPracticeExportMessage = true;
            } else {
                formData.requestError = true;
                formData.requestErrorMessage = "You do not have rights to export all.";
            }
            return;
        }

        if (patientId == 0) {
            formData.requestError = true;
            formData.requestErrorMessage = "Please select a Patient";
            return;
        }
        formData.requestError = false;
        formData.requestErrorMessage = "";
        const post_data = {
            patientId: patientId
        }
        const result = await ChoiceApi.ehiExport.addTaskListItem(post_data);
        if (result == undefined)
            return;
        if (result?.queuePatientRecordId != 0) {
            formData.activeBaseData.splice(0, 0, result);
            onHideNewExport();
        }
        else {
            formData.requestError = true;
            formData.requestErrorMessage = "Patient already exists.";
        }
    }

    async function onCancelExportRequestPractice() {
        formData.requestError = false;
        formData.requestErrorMessage = "";
        formData.showPracticeExportMessage = false;
    }

    async function onSaveExportRequestPractice() {
        formData.requestError = false;
        formData.requestErrorMessage = "";
        const post_data = {
            patientId: 0
        }
        const result = await ChoiceApi.ehiExport.addTaskListItem(post_data);
        if (result == undefined)
            return;
        if (result?.queuePatientRecordId != 0) {
            formData.activeBaseData.splice(0, 0, result);
        }
        else {
            onRefresh();
        }
        window.open("https://isalushealthcare.typeform.com/EhiExport");
        onHideNewExport();
    }

    async function onRefresh() {
        const result = await ChoiceApi.ehiExport.getTaskList(formData.tabName);
        formData.activeTopItem = 0;
        if (result == undefined) {
            return;
        }
        if (formData.tabName == EHIExportTabName.Active) {
            formData.activeBaseData = result.taskList;
        }
        else if (formData.tabName == EHIExportTabName.Inactive) {
            formData.inactiveBaseData = result.taskList;
        }
    }

    function GetMessageBadgeTitle(statusId: EHIExportStatus) {
        const config = messageBadgeConfiguration[statusId];
        if (config == undefined)
            return "Uknown";
        return config.tooltip;
    }
    function GetMessageBadgeOverrideStyle(statusId: EHIExportStatus) {
        const config = messageBadgeConfiguration[statusId];
        if (config == undefined)
            return "";
        return config.overrideStyle;
    }
    function GetMessageBadgeOverrideDisplay(statusId: EHIExportStatus) {
        const config = messageBadgeConfiguration[statusId];
        if (config == undefined)
            return "Unknown";
        return config.display;
    }

</script>

<style scoped>
    .fixedColumnHeader {
        width: 150px;
    }
    .downloadLinkBlue {
        color: #6495ed;
    }
    .statusYellow {
        background-color: #fff3cd;
        color: black;
    }
    .statusRed {
        background-color: #f8d7da;
        color: black;
    }
    .statusGreen {
        background-color: #d1e7dd;
        color: black;
    }
    .statusBlue {
        background-color: #cff4fc;
        color: black;
    }
</style>

<template>
    <ResizeMonitor @resize="windowResize" />
    <div class="row mt-2">
        <div class="fixedColumnHeader isalus-screen-header">
            EHI Export 
        </div>
        <div class="col">
            <ActionButtonV2 
                :action="ActionButtonStyle.Add" 
                text="New Export Request"
                :size="ButtonDisplaySize.Small" 
                :performAction="onNewExport"
                :disabled="!writePrivilege" />
        </div>
        <div class="col-auto">
            <ActionButtonV2 
                :action="ActionButtonStyle.Audit" 
                text="Audit"
                :size="ButtonDisplaySize.Small" 
                :performAction="onAudit" />
        </div>
    </div>
    <div class="row mt-2">
        <div class="col">
            EHI Export is where you can export patient data.  You can export data for a single patient or all patients within the practice with corresponding permissions.<br>Please note: <b>The file will export in 30 days.</b>
             <a href="https://officeemr.knowledgeowl.com/help/ehi-export" target="_blank" class="ps-2">Learn More</a>
        </div>
    </div>

    <div class="row mt-2">
        <div class="col">
            <Tab :value="EHIExportTabName.Active" v-model="formData.tabName" :layout="LayoutOrientation.Horz">                    
                Active
            </Tab>
            <Tab :value="EHIExportTabName.Inactive" v-model="formData.tabName" :layout="LayoutOrientation.Horz">                    
                Inactive
            </Tab>
        </div>
        <div class="col-auto">
            <ActionButtonV2 
                :action="ActionButtonStyle.Refresh" 
                text="Refresh"
                :size="ButtonDisplaySize.Small" 
                :spinText="'Refreshing'"
                :performAction="onRefresh" />
        </div>
    </div>

    <template v-if="formData.tabName == EHIExportTabName.Active">
        <div class="row mt-2">
            <div class="col">
                <ChoiceTable
                    :columns="activeTableColumns" 
                    :data="formData.activeBaseData" 
                    :show-filters="false" 
                    :allowSorting="false"
                    fontSize="smaller"
                    :itemsPerPage="formData.gridRowCount"                    
                    :showPaginationButtonsBelowChoiceTable="true"
                    :key-value-data-member="'queuePatientRecordId'">
                    <template #cell="{col, row, value}">
                        <template v-if="'Status' === col.Name">
                            <MessageBadge :title="GetMessageBadgeTitle(row.statusId)" :overrideStyle="GetMessageBadgeOverrideStyle(row.statusId)">
                                {{ GetMessageBadgeOverrideDisplay(row.statusId) }}
                            </MessageBadge>
                        </template>
                        <template v-if="'Action' === col.Name && writePrivilege">
                            <template v-if="row.linkCount > 0">
                                <Icon :icon="IconTypes.Export" class="downloadLinkBlue" :hoverCursor="HoverCursorTypes.Pointer" title="Export the zip file(s)." @click="onShowLinks(row as IEHIExportQueueItem)" :endingPadding="2"/>
                            </template>
                            <Icon :icon="IconTypes.Trashcan" :displayStyle="DisplayStyle.Danger" :hoverCursor="HoverCursorTypes.Pointer" title="Cancel this export." @click="onCancel(row as IEHIExportQueueItem)"/>
                        </template>
                    </template>
                </ChoiceTable>
            </div>
        </div>
    </template>
    <template v-if="formData.tabName == EHIExportTabName.Inactive">
        <div class="row mt-2">
            <div class="col">
                <ChoiceTable
                    :columns="inactiveTableColumns" 
                    :data="formData.inactiveBaseData" 
                    :show-filters="false" 
                    :allowSorting="false"
                    fontSize="smaller"
                    :itemsPerPage="formData.gridRowCount"                    
                    :showPaginationButtonsBelowChoiceTable="true"
                    :key-value-data-member="'queuePatientRecordId'">
                    <template #cell="{col, row, value}">
                        <template v-if="'Status' === col.Name">
                            <MessageBadge :title="GetMessageBadgeTitle(row.statusId)" :overrideStyle="GetMessageBadgeOverrideStyle(row.statusId)">
                                {{ GetMessageBadgeOverrideDisplay(row.statusId) }}
                            </MessageBadge>
                        </template>
                    </template>
                </ChoiceTable>
            </div>
        </div>
    </template>
    <ModalDialog :show="formData.showLinks" :size="ModalSize.Medium" @hide="onHideLinks">
        <template #title>Export Link(s)</template>
        <div class="row mb-2">
            <div class="col">
                Patient: {{ formData.currentPatientName }}
            </div>
        </div>
        <template v-for="item in formData.exportLinks" >
            <div class="row">
                <div class="col-11">
                    {{ item.linkName }}
                </div>
                <div class="col-1">
                    <a :href="BuildLink(item.linkId, item.linkName)">
                        <Icon 
                            :key="linkKey"
                            :icon="IconTypes.Export" 
                            :clickIcon="IconTypes.Complete"
                            class="downloadLinkBlue" 
                            :hoverCursor="HoverCursorTypes.Pointer" 
                            title="Export the zip file."></Icon>
                    </a><br>
                </div>
            </div>
        </template>
        <div class="row mt-2">
            <div class="col fst-italic">
                NOTE: Once you click the download icon, the icon will turn into a checkmark noting that the download process has begun.  The download may take some time to start.  You will see the download start at the top of the screen.
            </div>
        </div>
    </ModalDialog>

    <ModalDialog :show="formData.showNewExport" :size="ModalSize.Medium" @hide="onHideNewExport">
        <template #title>New Export Request</template>
        <div class="row">
            <div class="col isalus-section-header">
                Export Type
            </div>
        </div>
        <div class="row">
            <div class="col">
                <RadioButtonInput :value="EHIExportRequestType.Patient" v-model="formData.exportType" :layout="LayoutOrientation.Vert">                    
                    <div class="label-isalus-small">Single Patient</div>
                </RadioButtonInput>
                <RadioButtonInput :value="EHIExportRequestType.Practice" v-model="formData.exportType" :layout="LayoutOrientation.Vert">
                    <div class="label-isalus-small">All Patients</div>
                </RadioButtonInput>
            </div>
        </div>
        <template v-if="exportSinglePatient">
            <div class="row mt-2">
                <div class="col isalus-section-header">
                    Patient
                </div>
            </div>
            <div class="row">
                <div class="col">
                    <SearchPatient
                        :defaultSearchKey="formData.defaultPatientKey" 
                        :addToBaseClass="'label-isalus-small'"
                        label=""
                        @onChange="(val) => onPatientSearch(val)">                    
                    </SearchPatient> 
                </div>
            </div>
        </template>
        <template v-slot:footer>
            <template v-if="formData.showPracticeExportMessage">
               <div class="row">
                    <div class="col">
                        <span class="isalus-screen-header">Are you sure you wish to leave OfficeEMR?</span>
                        <p>You have initiated a Practice EHI Export, which has been built for migrating to a different EHR platform.</p>
                        <p>This will trigger a support case to cancel your services and obtain all of your practice EHI data. A member of our data team will be in contact to discuss the details of getting your data to you.</p>
                        <p>This will not immediately impact your access to your data or terminate your contract.</p>
                        <p>Do you wish to continue?</p>
                    </div>
                </div>
            </template>
            <MessageSection v-if="formData.requestError" :show-icon="false" :type="MessageSectionType.Text" :display-style="DisplayStyle.Danger">
                {{ formData.requestErrorMessage }}
            </MessageSection>
            <template v-if="!formData.showPracticeExportMessage">
                <ActionButtonV2 
                    :action="ActionButtonStyle.Close" 
                    text="Cancel"
                    :size="ButtonDisplaySize.Small" 
                    :displayStyle="ButtonDisplayStyle.OutlinePrimary" 
                    :performAction="onCancelExportRequest" />
                <ActionButtonV2 
                    :action="ActionButtonStyle.Complete" 
                    text="Request"
                    :size="ButtonDisplaySize.Small" 
                    :spinText="'Requesting'"
                    :performAction="onSaveExportRequest" />
            </template>
            <template v-if="formData.showPracticeExportMessage">
                <ActionButtonV2 
                    :action="ActionButtonStyle.Complete" 
                    text="Yes"
                    :size="ButtonDisplaySize.Small" 
                    :spinText="'Requesting'"
                    :displayStyle="ButtonDisplayStyle.OutlinePrimary" 
                    :performAction="onSaveExportRequestPractice" />
                <ActionButtonV2 
                    :action="ActionButtonStyle.Close" 
                    text="No"
                    :size="ButtonDisplaySize.Small" 
                    :performAction="onCancelExportRequestPractice" />
            </template>
        </template>    
    </ModalDialog>

    <ModalDialog :show="formData.showAudit" :size="ModalSize.XL" @hide="onHideAudit">
        <template #title>EHI Export Audit</template>
        <div class="row mt-2">
            <div class="col">
                <p style="overflow-x: scroll">                
                    <ChoiceTable
                        :columns="auditTableColumns" 
                        :data="formData.auditBaseData"
                        :height="500" 
                        :show-filters="true" 
                        :allowSorting="false"
                        fontSize="smaller">
                    </ChoiceTable>
                </p>
            </div>
        </div>
    </ModalDialog>

</template>

